import { AnimatePresence, motion } from 'framer-motion';
import React, { useRef, useState } from 'react';
import Map from '../../centered/components/Map';
import { Location } from '../../../location/LocationStep';
import { useResponsive } from '../../../shared/hooks/useResponsive';

interface ImmersiveLocationDisplayProps {
  locations: any[];
  selectedLocations: Location | Location[] | null;
  onLocationClick: (location: Location) => void;
  nodeData?: any;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  onNextPage?: () => void;
  onPrevPage?: () => void;
}

function isSelected(location: Location, selectedLocations: Location | Location[] | null): boolean {
  if (!selectedLocations) return false;
  if (Array.isArray(selectedLocations)) return selectedLocations.some(l => l.id === location.id);
  return selectedLocations.id === location.id;
}

function isLeftCard(index: number, count: number): boolean {
  if (count === 1) return true;
  if (count === 2) return index === 0;
  if (count === 3) return index !== 2;
  return index % 2 === 0;
}

function getGridStyle(count: number): React.CSSProperties {
  if (count === 1) return { display: 'grid', gridTemplateColumns: '1fr', gridTemplateRows: '1fr', width: '100%', height: '100%' };
  if (count === 2) return { display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr', width: '100%', height: '100%' };
  return { display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', width: '100%', height: '100%' };
}

function getCardStyle(index: number, count: number): React.CSSProperties {
  if (count === 3 && index === 0) return { gridColumn: '1 / -1' };
  return {};
}

const pageVariants = {
  enter: (dir: 'next' | 'prev') => ({ opacity: 0, y: dir === 'next' ? 60 : -60 }),
  center: { opacity: 1, y: 0 },
  exit: (dir: 'next' | 'prev') => ({ opacity: 0, y: dir === 'next' ? -60 : 60 }),
};

const ScrollIndicator: React.FC<{ direction: 'up' | 'down' }> = ({ direction }) => (
  <motion.div
    initial={{ opacity: 0, y: direction === 'down' ? 10 : -10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: direction === 'down' ? 10 : -10 }}
    transition={{ duration: 0.4 }}
    style={{
      position: 'absolute',
      [direction === 'down' ? 'bottom' : 'top']: 32,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 40,
      pointerEvents: 'none',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
    }}
  >
    {direction === 'up' && (
      <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase' }}>scroll</p>
    )}
    <div style={{ width: 1, height: 32, background: 'rgba(255,255,255,0.15)', position: 'relative', overflow: 'hidden', borderRadius: 1 }}>
      <motion.div
        animate={{ y: direction === 'up' ? ['100%', '-100%'] : ['-100%', '100%'] }}
        transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'rgba(255,255,255,0.7)', borderRadius: 1 }}
      />
    </div>
    {direction === 'down' && (
      <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase' }}>scroll</p>
    )}
  </motion.div>
);

const ImmersiveLocationDisplay: React.FC<ImmersiveLocationDisplayProps> = ({
  locations,
  selectedLocations,
  onLocationClick,
  hasNextPage = false,
  hasPrevPage = false,
  onNextPage,
  onPrevPage,
}) => {
  const { isMobile } = useResponsive();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [scrollDir, setScrollDir] = useState<'next' | 'prev'>('next');
  const [pageKey, setPageKey] = useState(0);
  const throttleRef = useRef(false);
  const mobileThrottleRef = useRef(false);

  const nodes: Location[] = locations.map(edge => edge.node);
  const count = nodes.length;

  const handleWheel = (e: React.WheelEvent) => {
    if (throttleRef.current) return;
    throttleRef.current = true;
    setTimeout(() => { throttleRef.current = false; }, 900);

    if (e.deltaY > 0 && hasNextPage) {
      setScrollDir('next');
      setPageKey(k => k + 1);
      onNextPage?.();
    } else if (e.deltaY < 0 && hasPrevPage) {
      setScrollDir('prev');
      setPageKey(k => k + 1);
      onPrevPage?.();
    }
  };

  const handleMobileScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (mobileThrottleRef.current) return;
    if (hasNextPage && el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
      mobileThrottleRef.current = true;
      setTimeout(() => { mobileThrottleRef.current = false; }, 1500);
      onNextPage?.();
    } else if (hasPrevPage && el.scrollTop <= 80) {
      mobileThrottleRef.current = true;
      setTimeout(() => { mobileThrottleRef.current = false; }, 1500);
      onPrevPage?.();
    }
  };

  // ── Mobile layout ──────────────────────────────────────────────────────────
  if (isMobile) {
    const mobileCardH = Math.max(220, Math.floor(window.innerHeight / nodes.length));
    return (
      <div style={{ width: '100%', height: '100vh', position: 'relative', background: '#080808', overflow: 'hidden' }}>
        <style>{`#lp-loc-list::-webkit-scrollbar { display: none; }`}</style>
        <div id="lp-loc-list" onScroll={handleMobileScroll} style={{ height: '100vh', overflowY: 'auto', scrollbarWidth: 'none' }}>
          {nodes.map((location) => {
            const selected = isSelected(location, selectedLocations);
            const lat = parseFloat(location.lat);
            const lng = parseFloat(location.long);
            return (
              <div
                key={location.id}
                onClick={() => onLocationClick(location)}
                style={{ position: 'relative', height: mobileCardH, overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.07)', cursor: 'pointer' }}
              >
                <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
                  <Map position={{ lat, lng }} onLocationChange={() => {}} disableInteraction zoom={14} showMarker={false} />
                </div>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.78) 100%)', zIndex: 1 }} />
                {selected && <div style={{ position: 'absolute', inset: 0, boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.55)', zIndex: 2 }} />}
                {selected && (
                  <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 3, width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#fff' }}>✓</div>
                )}
                <div style={{ position: 'absolute', bottom: 14, left: 16, right: 16, zIndex: 2 }}>
                  <p style={{ color: 'rgba(255,255,255,0.42)', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 4 }}>{location.country}</p>
                  <h3 style={{ color: '#fff', fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>{location.city}</h3>
                  {location.address && <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, marginTop: 2 }}>{location.address}</p>}
                </div>
              </div>
            );
          })}
        </div>

        <AnimatePresence>
          {hasPrevPage && <ScrollIndicator direction="up" />}
        </AnimatePresence>
        <AnimatePresence>
          {hasNextPage && <ScrollIndicator direction="down" />}
        </AnimatePresence>
      </div>
    );
  }

  // ── Desktop layout ──────────────────────────────────────────────────────────
  return (
    <div
      style={{ width: '100%', height: '100vh', overflow: 'hidden', position: 'relative' }}
      onWheel={handleWheel}
    >
      <AnimatePresence mode="wait" custom={scrollDir}>
        <motion.div
          key={pageKey}
          custom={scrollDir}
          variants={pageVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
          style={{ width: '100%', height: '100%' }}
        >
          <div style={getGridStyle(count)}>
            {nodes.map((location, index) => {
              const selected = isSelected(location, selectedLocations);
              const hovered = hoveredId === location.id;
              const lat = parseFloat(location.lat);
              const lng = parseFloat(location.long);
              const isLeft = isLeftCard(index, count);

              const textSide: React.CSSProperties = isLeft
                ? { left: '6%', maxWidth: '42%', textAlign: 'left' }
                : { right: '6%', maxWidth: '42%', textAlign: 'right' };

              const ctaSide: React.CSSProperties = isLeft
                ? { right: '6%', maxWidth: '42%', textAlign: 'right' }
                : { left: '6%', maxWidth: '42%', textAlign: 'left' };

              const ctaArrow = isLeft ? '←' : '→';

              return (
                <motion.div
                  key={location.id}
                  style={{ ...getCardStyle(index, count), position: 'relative', overflow: 'hidden', cursor: 'pointer', height: '100%' }}
                  onClick={() => onLocationClick(location)}
                  onHoverStart={() => setHoveredId(location.id)}
                  onHoverEnd={() => setHoveredId(null)}
                >
                  {/* Map */}
                  <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
                    <Map position={{ lat, lng }} onLocationChange={() => {}} disableInteraction={true} zoom={15} showMarker={false} />
                  </div>

                  {/* Dark overlay */}
                  <div style={{ position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none', background: 'rgba(0,0,0,0.42)' }} />

                  {/* Vignette */}
                  <div style={{
                    position: 'absolute', inset: 0, zIndex: 11, pointerEvents: 'none',
                    background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.45) 100%)',
                  }} />

                  {/* Hover shimmer */}
                  <motion.div
                    style={{ position: 'absolute', inset: 0, zIndex: 12, pointerEvents: 'none', background: 'rgba(255,255,255,0.05)' }}
                    animate={{ opacity: hovered ? 1 : 0 }}
                    transition={{ duration: 0.22 }}
                  />

                  {/* Selected ring */}
                  {selected && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      style={{ position: 'absolute', inset: 0, zIndex: 13, pointerEvents: 'none', boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.5)' }}
                    />
                  )}

                  {/* Pin at center */}
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 20, pointerEvents: 'none' }}>
                    <motion.div
                      animate={{ scale: hovered ? 1.6 : 1, opacity: hovered ? 0 : 0.35 }}
                      transition={{ duration: 0.5 }}
                      style={{ position: 'absolute', inset: -6, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.6)' }}
                    />
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff', boxShadow: '0 0 0 3px rgba(255,255,255,0.2), 0 2px 12px rgba(0,0,0,0.4)' }} />
                  </div>

                  {/* Main info text */}
                  <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 22, pointerEvents: 'none', ...textSide }}>
                    <motion.div animate={{ x: hovered ? (isLeft ? -5 : 5) : 0 }} transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}>
                      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 14 }}>
                        {location.country}
                      </p>
                      <h2 style={{ color: '#ffffff', fontSize: 52, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.0, marginBottom: 14, textShadow: '0 2px 16px rgba(0,0,0,0.6)' }}>
                        {location.city}
                      </h2>
                      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, lineHeight: 1.6, textShadow: '0 1px 6px rgba(0,0,0,0.5)' }}>
                        {location.address}
                      </p>
                    </motion.div>
                  </div>

                  {/* CTA opposite side */}
                  <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 22, pointerEvents: 'none', ...ctaSide }}>
                    <motion.div animate={{ opacity: hovered ? 1 : 0.28, x: hovered ? (isLeft ? 5 : -5) : 0 }} transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}>
                      <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, letterSpacing: '0.12em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: isLeft ? 'flex-end' : 'flex-start', gap: 10 }}>
                        {!isLeft && <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }}>{ctaArrow}</span>}
                        Book here
                        {isLeft && <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }}>{ctaArrow}</span>}
                      </p>
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Scroll up indicator — previous page */}
      <AnimatePresence>
        {hasPrevPage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            style={{
              position: 'absolute',
              top: 32,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 40,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
              scroll
            </p>
            <div style={{ width: 1, height: 32, background: 'rgba(255,255,255,0.15)', position: 'relative', overflow: 'hidden', borderRadius: 1 }}>
              <motion.div
                animate={{ y: ['100%', '-100%'] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'rgba(255,255,255,0.7)', borderRadius: 1 }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scroll down indicator — next page */}
      <AnimatePresence>
        {hasNextPage && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.4 }}
            style={{
              position: 'absolute',
              bottom: 32,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 40,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div style={{ width: 1, height: 32, background: 'rgba(255,255,255,0.15)', position: 'relative', overflow: 'hidden', borderRadius: 1 }}>
              <motion.div
                animate={{ y: ['-100%', '100%'] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'rgba(255,255,255,0.7)', borderRadius: 1 }}
              />
            </div>
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
              scroll
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImmersiveLocationDisplay;
